import React, { useMemo, useState, useCallback } from "react";
import { Listbox } from "@headlessui/react";
import { mdiArrowDown, mdiChevronDown, mdiMenuLeft, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import Checkbox from "components/Checkbox";
import { ITableHeaderList } from "models/HaulPay";
import styles from "./style.module.scss";
import { RefLoadingSpinner } from "../LoadingSpinner";
import usePaginatedInfiniteScroll from "hooks/usePaginatedInfiniteScroll";

interface ExpandableViewProps {
  row: Record<string, any>;
}

interface IProps {
  headers: ITableHeaderList[];
  data: Array<Record<string, any>>;
  totalCount?: number;
  nextLink?: boolean;
  prevLink?: boolean;
  handlePageChange?: (itemsPerPage: number, pageNumber?: number) => void;
  handleSelectAll?: VoidFunction;
  selectedList?: string[];
  errorList?: string[];
  handleClick?: (id: string) => void;
  onPrimaryClick?: (headerValue: string | string[], cellValue: any) => void;
  onSecondaryClick?: (headerValue: string | string[], cellValue: any) => void;
  sortDisableList?: string[];
  isShowChecked?: boolean;
  tableBodyClass?: string;
  className?: string;
  fetchNextPage?: () => void;
  fetchPrevPage?: () => void;
  setPageSize?: (pageSize: number) => void;
  goToPage?: (pageNumber: number) => void;
  loading?: boolean; // Added loading prop
  infiniteScroll?: boolean;
  ExpandableView?: React.FC<ExpandableViewProps>;
  simple?: boolean;
}

export default function DataTable({
  headers,
  data,
  handleSelectAll,
  selectedList,
  errorList,
  totalCount,
  nextLink = false,
  prevLink = false,
  handlePageChange,
  handleClick,
  onPrimaryClick,
  onSecondaryClick,
  sortDisableList,
  isShowChecked = false,
  tableBodyClass,
  className,
  fetchPrevPage,
  fetchNextPage,
  setPageSize,
  goToPage,
  loading = false, // Default value for loading prop
  infiniteScroll,
  ExpandableView,
  simple
}: Readonly<IProps>) {
  const dataLength = data.length;
  const [sortKey, setSortKey] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pageIndex, setPageIndex] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false); // State for loading more data
  const [expand, setExpand] = useState<Record<number, boolean>>({});

  const handleLoadMore = useCallback(async () => {
    if (fetchNextPage && nextLink) {
      setIsLoadingMore(true);
      await fetchNextPage();
      setIsLoadingMore(false);
    }
  }, [fetchNextPage, nextLink]);

  const { containerRef, loadMoreRef } = usePaginatedInfiniteScroll({
    loadMore: handleLoadMore,
    hasMore: nextLink,
    loading: isLoadingMore || loading,
    threshold: 300
  });

  // Handle Checked Items
  function handleChecked(id: string) {
    if (handleClick) {
      handleClick(id);
    }
  }

  const isSelected = (id: string): boolean | undefined => {
    if (selectedList) {
      return selectedList.includes(id);
    }
    return false;
  };

  function sortBy(key: string) {
    if (!sortDisableList?.includes(key)) setSortKey((prev) => (prev === key ? "-" + key : key));
  }

  function getNestedValue(row: any, keyPath: string): any {
    const keys = keyPath.split(".");
    let nestedAcc = row;

    for (const key of keys) {
      nestedAcc = nestedAcc?.[key];
      if (nestedAcc === undefined) {
        return undefined;
      }
    }

    return nestedAcc;
  }

  const totalPageNumbers = useMemo(
    () => (totalCount === 0 ? 1 : Math.ceil((totalCount ?? 1) / itemsPerPage)),
    [itemsPerPage, totalCount]
  );

  const sortedTableData = useMemo(() => {
    const result = [...data];
    if (sortKey) {
      result.sort((a, b) => {
        const key = sortKey.replace("-", "");
        const direction = sortKey.startsWith("-") ? -1 : 1;
        const aValue = getNestedValue(a, key);
        const bValue = getNestedValue(b, key);

        if (aValue < bValue) return -1 * direction;
        if (aValue > bValue) return 1 * direction;
        return 0;
      });
    }

    return result;
  }, [sortKey, data]);

  const { startIndex, lastIndex } = useMemo(() => {
    if (dataLength === 0) return { startIndex: 0, lastIndex: 0 };

    if (totalCount && itemsPerPage > totalCount) setPageIndex(1);

    const startIndex = (pageIndex - 1) * itemsPerPage + 1;
    let lastIndex;
    if (pageIndex === totalPageNumbers) {
      lastIndex = totalCount ? totalCount : dataLength;
    } else {
      lastIndex = itemsPerPage * pageIndex;
    }

    return { startIndex, lastIndex };
  }, [dataLength, itemsPerPage, pageIndex, totalCount, totalPageNumbers]);

  const primaryClick = (value: Record<string, any> | string, header: ITableHeaderList) => {
    return onPrimaryClick ? () => onPrimaryClick(header.value, value) : () => {};
  };

  const secondaryClick = (value: Record<string, any> | string, header: ITableHeaderList) => {
    return onSecondaryClick ? () => onSecondaryClick(header.value, value) : () => {};
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setPageIndex(1);
    if (handlePageChange && totalCount)
      handlePageChange(value < totalCount ? value : totalCount, 1);
  };

  // Pagination Functions (if not using infinite scroll)
  function goNextPage() {
    if (nextLink && handlePageChange) {
      setPageIndex(pageIndex + 1);
      handlePageChange(itemsPerPage, pageIndex + 1);
    }
    if (fetchNextPage) {
      setPageIndex(pageIndex + 1);
      fetchNextPage();
    }
  }

  function goPreviousPage() {
    if (prevLink && handlePageChange) {
      setPageIndex(pageIndex - 1);
      handlePageChange(itemsPerPage, pageIndex - 1);
    }
    if (fetchPrevPage) {
      setPageIndex(pageIndex - 1);
      fetchPrevPage();
    }
  }

  if (simple) {
    const visibleHeaders = headers.filter((header) => header.isShow !== false);
    const normalHeaders = visibleHeaders.filter((header) => !header.fullHorizontal);
    const fullHorizontalHeaders = visibleHeaders.filter((header) => header.fullHorizontal);

    return (
      <table className="w-full table-auto">
        <thead className="whitespace-pre bg-input">
          {normalHeaders.length > 0 && (
            <tr>
              {normalHeaders.map((header, index) => (
                <th
                  key={String(header.value)}
                  className={clsx(
                    index === 0 && "pl-2",
                    header.rowStyle,
                    errorList?.length === 0 && header.isShow && "hidden"
                  )}>
                  <button
                    type="button"
                    className={clsx(styles.tableHeaderWrapper, header.rowStyle)}>
                    <span className="text-wrap">{header.label}</span>
                  </button>
                </th>
              ))}
            </tr>
          )}
          {fullHorizontalHeaders.map((header, idx) => (
            <tr key={`fullHorizontal-${idx}`}>
              <th colSpan={normalHeaders.length} className={clsx(header.rowStyle)}>
                <span className="text-wrap">{header.label}</span>
              </th>
            </tr>
          ))}
        </thead>

        <tbody className={clsx(styles.bodyContainer, tableBodyClass, "divide-y divide-gray-700")}>
          {sortedTableData.map((row, rowIndex) => (
            <React.Fragment key={row.id}>
              {/* Normal data row */}
              <tr className={clsx("h-full", errorList?.includes(row.id) && "bg-red-300")}>
                {normalHeaders.map((header, headerIndex) => {
                  let value: any;
                  if (Array.isArray(header.value)) {
                    value = header.value.reduce((acc: Record<string, any>, keyPath: string) => {
                      const keyValue = getNestedValue(row, keyPath);
                      const keyName = keyPath.split(".").pop() ?? keyPath;
                      acc[keyName] = keyValue;
                      return acc;
                    }, {});
                  } else {
                    const keys = header.value.split(".");
                    value = keys.reduce((acc: any, key: string) => acc?.[key], row);
                  }
                  return (
                    <td
                      colSpan={header.colSpan ?? 1}
                      key={String(header.value)}
                      className={clsx(
                        "px-2 py-3 h-full",
                        !isShowChecked && headerIndex === 0 && "pl-4",
                        header.rowStyle,
                        errorList?.length === 0 && header.isShow && "hidden",
                        !errorList?.includes(row.id) && header.isShow && "!opacity-0"
                      )}>
                      <div className="h-full">
                        {header.render
                          ? header.render(
                              value,
                              rowIndex,
                              row.id,
                              primaryClick(value, header),
                              secondaryClick(value, header)
                            )
                          : value}
                      </div>
                    </td>
                  );
                })}
              </tr>
              {fullHorizontalHeaders.map((header, idx) => (
                <tr key={`${row.id}-fullHorizontal-${idx}`}>
                  <td colSpan={normalHeaders.length} className={clsx(header.rowStyle)}>
                    {header.render ? (
                      header.render(
                        row,
                        rowIndex,
                        row.id,
                        primaryClick(row, header),
                        secondaryClick(row, header)
                      )
                    ) : (
                      <span className="text-wrap">{header.label || "\u00A0"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <div className={clsx("flex flex-col relative", className)}>
      <div
        ref={containerRef} // Attach the container ref for scrolling
        className="overflow-y-auto flex-grow h-full rounded-xl bg-background"
        style={{ minHeight: "400px" }}>
        <table className="w-full table-auto">
          <thead className="whitespace-pre bg-input">
            <tr>
              {handleSelectAll ? (
                <th className="pl-3">
                  <Checkbox
                    checked={
                      sortedTableData.length > 0 && selectedList?.length === sortedTableData.length
                    }
                    onChange={() => handleSelectAll()}
                  />
                </th>
              ) : null}
              {headers
                .filter((item) => item.isShow !== false)
                .map((header, index) => (
                  <th
                    key={header.value as any}
                    className={clsx(
                      index === 0 && "pl-2",
                      header.rowStyle,
                      errorList?.length === 0 && header.isShow && "hidden"
                    )}>
                    <button
                      type="button"
                      onClick={() =>
                        sortBy(Array.isArray(header.value) ? header.value[0] : header.value)
                      }
                      className={clsx(styles.tableHeaderWrapper, header.rowStyle)}>
                      <span className="text-wrap">{header.label}</span>
                      {!sortDisableList?.includes(
                        Array.isArray(header.value) ? header.value[0] : header.value
                      ) && (
                        <Icon
                          path={mdiArrowDown}
                          className={clsx(styles.sortIcon, {
                            "rotate-180": sortKey === header.value,
                            "rotate-0": sortKey === "-" + header.value,
                            "opacity-20": sortKey !== header.value && sortKey !== "-" + header.value
                          })}
                        />
                      )}
                    </button>
                  </th>
                ))}
            </tr>
          </thead>

          <tbody className={styles.bodyContainer}>
            {sortedTableData.length ? (
              sortedTableData.map((row, index) => (
                <>
                  <tr
                    key={row.id} // Use a stable key from your data
                    className={clsx(tableBodyClass, errorList?.includes(row.id) && "bg-red-300")}>
                    {isShowChecked && (
                      <td className="pl-3">
                        <Checkbox
                          checked={isSelected(row.id)}
                          onChange={() => handleChecked(row.id)}
                        />
                      </td>
                    )}
                    {headers
                      .filter((item) => item.isShow !== false)
                      .map((header, headerIndex) => {
                        let value: any;
                        if (Array.isArray(header.value)) {
                          value = header.value.reduce(
                            (acc: Record<string, any>, keyPath: string) => {
                              const keyValue = getNestedValue(row, keyPath);
                              const keyName = keyPath.split(".").pop() ?? keyPath;
                              acc[keyName] = keyValue;
                              return acc;
                            },
                            {}
                          );
                        } else {
                          const keys = header.value.split(".");
                          value = keys.reduce((acc: any, key: string) => acc?.[key], row);
                        }
                        return (
                          <td
                            key={header.value as any}
                            onClick={
                              ExpandableView
                                ? () =>
                                    setExpand((prevState) => ({
                                      ...prevState,
                                      [index]: !prevState[index]
                                    }))
                                : undefined
                            }
                            className={clsx(
                              "px-2 py-3 h-full",
                              !isShowChecked && headerIndex === 0 && "pl-4",
                              header.rowStyle,
                              errorList?.length === 0 && header.isShow && "hidden",
                              !errorList?.includes(row.id) && header.isShow && "!opacity-0"
                            )}>
                            <div className="h-full">
                              {header.render
                                ? header.render(
                                    value,
                                    index,
                                    row.id,
                                    primaryClick(value, header),
                                    secondaryClick(value, header)
                                  )
                                : value}
                            </div>
                          </td>
                        );
                      })}
                  </tr>
                  {expand[index] && ExpandableView && (
                    <tr>
                      <td colSpan={headers.length + (isShowChecked ? 1 : 0)}>
                        <ExpandableView row={row} />
                      </td>
                    </tr>
                  )}
                </>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length + (isShowChecked ? 1 : 0)}>
                  {!loading && <div className={styles.emptyDataRow}>No Data</div>}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Sentinel Element for Infinite Scroll */}
        {infiniteScroll && data.length > 0 && (
          <div className="py-3">
            <RefLoadingSpinner observerRef={loadMoreRef} isLoadingMore={isLoadingMore || loading} />
          </div>
        )}
      </div>

      {/* Pagination Footer (Visible Only When Not Using Infinite Scroll) */}
      {!infiniteScroll && totalPageNumbers > 1 && (
        <div className={styles.footer}>
          <Listbox
            value={itemsPerPage}
            onChange={(value) => {
              setPageSize && setPageSize(value);
              handleItemsPerPageChange(value);
            }}
            as="div"
            className="relative">
            <Listbox.Button className={clsx(styles.itemsPerPageButton, "border-r")}>
              {itemsPerPage}
              <Icon path={mdiChevronDown} />
            </Listbox.Button>

            <Listbox.Options className={styles.itemsPerPageOptions}>
              {[20, 50, 100].map((number) => (
                <Listbox.Option key={number} value={number} className={styles.option}>
                  {number}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Listbox>

          <div className={styles.description}>
            {startIndex} - {lastIndex} items of{" "}
            {totalCount && lastIndex < totalCount ? totalCount : lastIndex} items
          </div>

          <Listbox
            value={pageIndex}
            onChange={(value) => {
              goToPage && goToPage(value);
              setPageIndex(value);
              handlePageChange && handlePageChange(itemsPerPage, value);
            }}
            as="div"
            className="relative">
            <Listbox.Button className={clsx(styles.itemsPerPageButton, "border-l")}>
              {pageIndex}
              <Icon path={mdiChevronDown} />
            </Listbox.Button>
            <Listbox.Options className={styles.itemsPerPageOptions}>
              {[...Array(totalPageNumbers)].map((_, index) => (
                <Listbox.Option key={index + 1} value={index + 1} className={styles.option}>
                  {index + 1}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Listbox>

          <div className={styles.totalPageNumbers}>of {totalPageNumbers} pages</div>

          <button
            type="button"
            disabled={!prevLink}
            className={styles.menuLeftButton}
            onClick={goPreviousPage}>
            <Icon path={mdiMenuLeft} className="m-auto" />
          </button>

          <button
            type="button"
            disabled={!nextLink}
            className={styles.menuRightButton}
            onClick={goNextPage}>
            <Icon path={mdiMenuLeft} className="m-auto rotate-180" />
          </button>
        </div>
      )}

      {/* Loading Overlay for Non-Infinite Scroll Mode */}
      {((!infiniteScroll && loading) || (infiniteScroll && data.length === 0 && loading)) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-400 bg-opacity-30">
          <Icon path={mdiLoading} spin size={3} className="text-white" />
        </div>
      )}
    </div>
  );
}
